/**
 * 上传
 * 1.单张、多张
 * 2.查看√
 * 2.1.弹窗：全屏、分享、关闭弹窗√
 * 2.2.多张弹窗：轮播图
 * 3.旋转功能
 * 4.删除
 */
import React, {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import { exportImgUrl } from "@/lib/commonService";
import withPhotoSwiper, { IWithPhotoSwiperProps } from "./WithPhotoSwiper";

import {
    FmButton,
    FmIcon,
    FmImage,
    FmLink,
    FmLoading,
    FmToast,
} from "@/ui-component";

import stl from "./upload.module.scss";
import classnames from "classnames";
import { isEmpty } from "lodash";
import { compress, getFileHash, getS3Folder } from "./utils";
import { useTranslation } from "@/src/i18n/client";
import { uploadImg } from "@/src/models/uploadImg";

const webpRegexp = /\.webp/;

interface IImgInfo {
    blob: Blob | null | string;
    fileType: string;
    fileName: string;
    file: File;
}

interface ILicenceData {
    signature: string;
    policyBase64: string;
}

const defaultProps = {
    uploading: false,
    limitPhotoNum: 1,
    fullUrl: false,
    halfUrl: false,
    onlyName: false,
    acceptPdf: false,
    noZip: false,
    upWebp: false,
    multiple: true,
};

/**
 *接受参数如下
 *1.change：func，回传上传成功的url，必填
 *2.changeUploading:func，改变uploading的值，必填
 *3.uploading:boolean，表示是否正在上传，必填
 *4.folder:string,填上传文件的S3bucket文件夹，默认是'images/users'
 *5.multiple:boolean是否多张图片上传
 *6.limitPhotoNum:number限制图片上传张数
 *7.noZip:是否压缩图片
 *8.upWebp:是否上传webp图片
 * @class Upload
 * @extends
 */

interface IUploadComponentProps {
    fullUrl?: boolean; //全路径
    halfUrl?: boolean; //半路径，不含host
    onlyName?: boolean; //只有名字
    multiple?: boolean; //是否多张图片上传
    limitPhotoNum?: number; //制图片上传张数
    acceptPdf?: boolean; //是否接受pdf文件
    noZip?: boolean; //是否压缩图片
    upWebp?: boolean; //是否上传webp图片

    uploading?: boolean;
    imgUrl?: string[] | string;
    uploadContent?: ReactNode;
    change?: (imgUrl?: string[] | string | undefined, webUrl?: any) => void;
    changeUploading?: (uploading: boolean) => void;
    folder?: string;
}
type IUploadProps = IWithPhotoSwiperProps<IUploadComponentProps> &
    IUploadComponentProps;

const UploadComponent: React.FC<IUploadProps> = (prop) => {
    const props = useMemo(() => {
        return {
            ...defaultProps,
            ...prop,
        };
    }, [prop]);
    const { t } = useTranslation("common");
    const [hasFile, setHasFile] = useState(false);
    const [img, setImg] = useState<string[]>([]);
    const [degree, setDegree] = useState(0);
    const [files, setFiles] = useState<FileList | null | undefined>();
    const [toastVisible, setToastVisible] = useState(false);
    const [toastChildren, setToastChildren] = useState("");

    useEffect(() => {
        // hasFile 默认图片
        setHasFile(true);
        if (props.imgUrl) {
            Array.isArray(props.imgUrl)
                ? setImg(props.imgUrl)
                : setImg([props.imgUrl]);
        }
    }, [props.imgUrl]);

    const {
        multiple,
        uploading,
        acceptPdf,
        imgUrl,
        limitPhotoNum,
        uploadContent,
        fullUrl,
        halfUrl,
        onlyName,
        noZip,
        upWebp,
        change,
        changeUploading,
        openSetPhotoSwiper,
        mapPhotoSwiperImg,
    } = props;

    const imgs = useMemo(() => {
        return mapPhotoSwiperImg!(img.filter((item) => !/pdf/.test(item)));
    }, [mapPhotoSwiperImg, img]);

    /**
     * 发送数据给亚马逊s3，获得图片地址
     * @param {*} blob 图片blob对象
     * @param {*} fileType 文件类型
     * @param {*} sign 亚马逊标志
     * @param {*} fileName 文件名
     */
    const submit = useCallback(
        (
            formDataObj: Record<string, any>,
            blob: Blob | string,
            fileType: string,
            fileName: string,
            files: File,
        ) => {
            const fd: Record<string, any> = {
                key: fileName,
                acl: "public-read",
                "Content-Type": fileType,
                "Cache-Control": "max-age=31536000", //缓存时间,
                file: blob,
                path: fileName,
            };

            return uploadImg({ ...fd, ...formDataObj })
                .then((res) => {
                    let img = res.fullPath.replace(/%2F/g, "/");
                    //预发环境欠缺，为了显示图片，修改如下
                    return img;
                })
                .catch((err) => {
                    setToastVisible(true);
                    setToastChildren(
                        err.message ||
                            "uploading false,please wait a moment and try again.",
                    );
                });
        },
        [],
    );
    const deleteImg = useCallback(
        (index: number) => {
            setDegree(0);
            if (img.length > 1) {
                img.splice(index, 1);
                setImg(img);
                change?.(img);
            } else if (img.length === 1) {
                setImg([]);
                setHasFile(false);
                change?.();
            }
        },
        [change, img],
    );
    /**
     * 1.读取文件，
     * 2.使用canvas压缩图片，
     * 3，使用blob对象二进制化文件。
     * 4.通信node接口获取 亚马逊licence（在下一步使用）
     * 5.上传blob二进制文件到 亚马逊，获取图片路径
     * 6.返回图片路径
     * @param {*} file 上传的文件
     * @returns promise
     */

    const imgChoose = useCallback(
        async (file: File, formDataObj: Record<string, any>) => {
            const folder = props.folder ?? "images/users";
            props.changeUploading?.(true);

            let fileType = file.type; //文件类型
            let fileName;
            const arr = fileType.split("/");
            fileType = arr[arr.length - 1];
            if (acceptPdf && file.type.includes("pdf")) {
                // TODO: pdf后面再考虑好了，现在把pdf模块干掉，原来是走compress的
                return;
            }
            const prom = compress(file, !!acceptPdf, degree, !!noZip, !!upWebp); //压缩文件，返回{ img, webpimg }的base64的Promise
            let blob: Blob | null = null; //普通图片blob
            let webpBlob: string | Blob | null = null; //webp图片blob
            return prom
                .then((res) => {
                    const { img, webpImg } = res;
                    blob = img;
                    webpBlob = webpImg;
                    return res;
                })
                .then((res) => {
                    const folderRes = getS3Folder(folder);
                    const folderName =
                        typeof folderRes === "string"
                            ? folderRes
                            : folderRes.folder;
                    // 获取licence
                    //存储上传文件信息
                    let imgsInfoArr: IImgInfo[] = [];
                    //普通图片 licence
                    fileName =
                        folderName + getFileHash(file.name) + "." + fileType;
                    imgsInfoArr.push({
                        blob,
                        fileType: file.type,
                        fileName,
                        file,
                    });
                    //webp图片 licence
                    if (upWebp && webpBlob) {
                        const webpFileName =
                            folderName + getFileHash(file.name) + ".webp";

                        imgsInfoArr.push({
                            blob: webpBlob,
                            fileType: "image/webp",
                            fileName: webpFileName,
                            file,
                        });
                    }
                    return imgsInfoArr;
                })
                .then((res) => {
                    if (res?.length) {
                        const submitProsArr: any[] = [];
                        res.forEach((item) => {
                            const { blob, fileType, fileName, file } = item;
                            submitProsArr.push(
                                submit(
                                    formDataObj,
                                    blob!,
                                    fileType,
                                    fileName,
                                    file,
                                ),
                            );
                        });
                        return Promise.all(submitProsArr).then((uploadRes) => {
                            return uploadRes;
                        });
                    }
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        [acceptPdf, degree, noZip, props, submit, upWebp],
    );

    // 选择完图片
    const multipleImgChoose = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.target.files?.length) {
                return;
            }

            let files = e.target.files;

            const formDataArr = Array.from(files).map((file) => {
                return {
                    [e.target.name]: file,
                };
            });
            const formDataObj = {}; // formData 全部处理成对象
            formDataArr.forEach((item) => {
                Object.assign(formDataObj, item);
            });

            setFiles(files);
            let promises = []; //缓存上传promise
            // 多选
            if (multiple && limitPhotoNum > 1) {
                // 多选
                // 过滤多余图片
                files = Array.prototype.filter.call(
                    files,
                    (item, index) => index < limitPhotoNum - img.length,
                ) as unknown as FileList;
                let index = 0;
                while (index < files.length) {
                    if (index < limitPhotoNum) {
                        promises.push(imgChoose(files[index], formDataObj));
                    }
                    index++;
                }
            } else {
                //单选
                promises.push(imgChoose(files[0], formDataObj));
            }
            try {
                let res = await Promise.all(promises); //获取多个promise对象

                //res 里面数据结构[[xx.jgp,xxx.webp]]/[[xx.jpg]];
                res = res.filter((item) => !!item); //去掉空子项
                //获取要显示的图片只显示普通图片
                //普通图片
                let commonImgs: any[] = [];
                //webp图片
                let webpImgs: any[] = [];
                //遍历获得普通图片，webp图片
                res.forEach((item) => {
                    if (item) {
                        for (let it of item) {
                            if (webpRegexp.test(it)) {
                                webpImgs.push(it);
                            } else {
                                commonImgs.push(it);
                            }
                        }
                    }
                });
                if (!multiple) {
                    setHasFile(!isEmpty(res)); // 显示图片与否 !isEmpty(res) ？ true : false
                    setImg(commonImgs);
                } else {
                    if (!isEmpty(res) && img.length < limitPhotoNum) {
                        setHasFile(true);
                        setImg(img.concat(commonImgs));
                    }
                }
                //获取相对路径，后台需要
                commonImgs = commonImgs.map((item) =>
                    exportImgUrl({
                        fullUrl: !!fullUrl,
                        halfUrl: !!halfUrl,
                        onlyName: !!onlyName,
                        url: item,
                    }),
                );
                webpImgs = webpImgs.map((item) =>
                    exportImgUrl({
                        fullUrl: !!fullUrl,
                        halfUrl: !!halfUrl,
                        onlyName: !!onlyName,
                        url: item,
                    }),
                );
                //触发文件改变回调
                if (multiple && limitPhotoNum > 1) {
                    change?.(img, webpImgs);
                } else {
                    change?.(commonImgs[0], webpImgs[0]);
                }
                changeUploading?.(false); //改变上传状态
            } catch (e: any) {
                setToastChildren(e);
                setToastVisible(true);
                setHasFile(false);
                changeUploading?.(false);
            }
        },
        [
            change,
            changeUploading,
            fullUrl,
            halfUrl,
            img,
            imgChoose,
            limitPhotoNum,
            multiple,
            onlyName,
        ],
    );

    const rotate = useCallback(
        (singleImg: string) => {
            const file = files![0];
            setDegree((prev) => prev + 1);
            setHasFile(false);
            setImg([]);

            const promise = imgChoose(file, {});
            promise
                .then((res) => {
                    //普通图片 webp图片
                    if (!res) {
                        throw Error("no rotate images"); // 走catch模块
                    }

                    let commonImg, webpImg;
                    for (let item of res) {
                        if (webpRegexp.test(item)) {
                            webpImg = item;
                        } else {
                            commonImg = item;
                        }
                    }

                    setImg([commonImg]);
                    setHasFile(true);
                    changeUploading?.(false);
                    const comImgUrl = commonImg
                        ? exportImgUrl({
                              fullUrl: !!fullUrl,
                              halfUrl: !!halfUrl,
                              onlyName: !!onlyName,
                              url: commonImg,
                          })
                        : "";
                    const webpImgUrl = webpImg
                        ? exportImgUrl({
                              fullUrl: !!fullUrl,
                              halfUrl: !!halfUrl,
                              onlyName: !!onlyName,
                              url: webpImg,
                          })
                        : "";
                    //获取相对路径，后台需要
                    //触发文件改变回调
                    change?.(comImgUrl, webpImgUrl);
                })
                .catch((e) => {
                    setToastChildren(e);
                    setToastVisible(true);
                    setImg([]);
                    setHasFile(false);
                    changeUploading?.(false);
                    change?.();
                });
        },
        [change, changeUploading, files, fullUrl, halfUrl, imgChoose, onlyName],
    );

    const showExitImage = useMemo(() => {
        return img.map((item, index) => {
            return (
                <div
                    key={item}
                    className={classnames(
                        stl["show-exit-image"],
                        hasFile ? stl["preview-img-container"] : "hidden",
                    )}
                >
                    <div className={classnames(stl["upload-preview"])}>
                        {item.indexOf("pdf") === item.length - 3 ? (
                            <FmLink
                                href={item}
                                target="_blank"
                            >{`pdf file`}</FmLink>
                        ) : (
                            <>
                                <FmImage
                                    className={stl["upload-preview-image"]}
                                    image={item}
                                    onClick={() =>
                                        openSetPhotoSwiper!(true, imgs, index)
                                    }
                                />
                                {/* 旋转 多图片暂不支持, 已经上传的暂不支持 */}
                                {(multiple && limitPhotoNum > 1) ||
                                    (!imgUrl && (
                                        <FmButton
                                            className={classnames(
                                                stl["upload-rotate-container"],
                                            )}
                                            onClick={(e) => rotate(item)}
                                            icon={"icon--rotate"}
                                            iconClassName={
                                                stl["icon-rotate-class"]
                                            }
                                            variant={"text"}
                                            iconLeft
                                            contentClassName={
                                                stl["rotate-content-btn"]
                                            }
                                            buttonColor="#000"
                                        >
                                            <span
                                                className={stl["rotate-text"]}
                                            >
                                                {t("common:ROTATE")}
                                            </span>
                                        </FmButton>
                                    ))}
                            </>
                        )}
                    </div>
                    <div
                        className={classnames(stl["upload-close-icon"])}
                        onClick={(e) => deleteImg(index)}
                    >
                        <span
                            className={classnames(stl["close-icon-bar"])}
                        ></span>
                        <span
                            className={classnames(
                                stl["close-icon-bar"],
                                stl["close-icon-bar-trans"],
                            )}
                        ></span>
                    </div>
                </div>
            );
        });
    }, [
        deleteImg,
        hasFile,
        img,
        imgUrl,
        imgs,
        limitPhotoNum,
        multiple,
        openSetPhotoSwiper,
        rotate,
        t,
    ]);
    const uploadingDom = useMemo(() => {
        return uploading ? null : (
            <input
                className={classnames(stl["review-update-input"])}
                type="file"
                name="file"
                multiple={props.multiple}
                onChange={(e) => multipleImgChoose(e)}
                accept={`image/*, ${acceptPdf ? ".pdf" : ""}`}
            />
        );
    }, [acceptPdf, multipleImgChoose, props.multiple, uploading]);

    const uploadingOrOutOfLimit = useMemo(() => {
        return (
            ((uploading || hasFile) && (!multiple || uploading)) ||
            (multiple && img.length >= limitPhotoNum)
        );
    }, [hasFile, img.length, limitPhotoNum, multiple, uploading]);
    return (
        <>
            <FmToast
                seconds={3}
                visible={toastVisible}
                onChange={(visible: boolean) => setToastVisible(visible)}
            >
                {toastChildren}
            </FmToast>

            <div
                className={classnames(
                    stl["upload-preview-img-contaner"],
                    stl["upload-container"],
                    "upload-preview-img-contaner",
                )}
            >
                {/* loading状态先显示加载图标 */}
                <span style={{ color: "grey.500" }}>
                    <FmLoading
                        visible={uploading}
                        className={classnames(
                            stl["loading-style"],
                            "show-loading",
                        )}
                    />
                </span>
                {/* 展示已有图片 */}
                {showExitImage}
                {/* 正在传或有默认文件和非多图或正在传时隐藏,  正在传多图时数量超过限制 */}
                <div
                    className={classnames(
                        { ["hidden"]: uploadingOrOutOfLimit },
                        stl["out-of-limit-or-uploading"],
                        "out-of-limit-or-uploading",
                    )}
                >
                    {/* 有定制按钮的展示定制按钮 */}
                    {uploadContent ?? (
                        <div
                            className={classnames(
                                stl["review-update-icon"],
                                "review-update-icon",
                            )}
                        >
                            <FmIcon
                                icon={"icon--upload"}
                                className={stl["review-uploading-icon"]}
                            />
                        </div>
                    )}
                    {/* 不在上传loading状态时，上传功能才可用 */}
                    {uploadingDom}
                </div>
            </div>
        </>
    );
};

const Upload = withPhotoSwiper(UploadComponent)();
export default Upload;
export { type IUploadProps };
